import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../lib/api'
import UnifiedAnamnesisForm from '../components/intake/UnifiedAnamnesisForm'
import { buildAppointmentPayload, initialAnamnesisForm } from '../components/intake/anamnesisSchema'
import Button from '../components/ui/Button'
import { Card } from '../components/ui/Card'

const STORAGE_KEY = 'mathkids.localAppointments'
const slotsByProfessional = {
  'fallback-psico-1': ['09:00', '10:30', '14:00'],
  'fallback-psicoped-1': ['08:30', '13:30', '16:00'],
  'fallback-neuro-1': ['11:00', '15:00', '18:00']
}


function getFallbackProfessionals() {
  return [
    { id: 'fallback-psico-1', name: 'Dra. Marina Nunes', specialty: 'Psicologia infantil', specialization: 'Anamnese, autorregulação e ansiedade matemática.', bio: 'Atende crianças do 1º ao 9º ano com foco em vínculo, aprendizagem e devolutiva prática para família e escola.', rating: 4.9, reviews: 128, hourlyRate: 180, verified: true },
    { id: 'fallback-psicoped-1', name: 'Profª Camila Rocha', specialty: 'Psicopedagogia clínica', specialization: 'ABA, material dourado e adaptação de trilhas BNCC.', bio: 'Trabalha com mediação concreta, microetapas, recursos visuais e integração com programação em blocos e games.', rating: 4.8, reviews: 94, hourlyRate: 165, verified: true },
    { id: 'fallback-neuro-1', name: 'Dr. Felipe Moura', specialty: 'Neuropsicologia educacional', specialization: 'Funções executivas, TDAH e avaliação inicial.', bio: 'Ajuda a mapear atenção, organização da rotina e barreiras de entrada para a matemática.', rating: 4.7, reviews: 71, hourlyRate: 210, verified: true }
  ]
}

function saveLocalAppointment(payload) {
  const current = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
  current.unshift({ id: `local-${Date.now()}`, createdAt: new Date().toISOString(), ...payload })
  localStorage.setItem(STORAGE_KEY, JSON.stringify(current.slice(0, 30)))
}

export default function ProfessionalsPage() {
  const navigate = useNavigate()
  const [professionals, setProfessionals] = useState([])
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState('')
  const [selectedProfessional, setSelectedProfessional] = useState(null)
  const [form, setForm] = useState(initialAnamnesisForm)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const fetchProfessionals = async () => {
      try {
        setLoading(true)
        const response = await api.get('/professionals')
        const list = response.data.professionals || []
        const merged = [...list]
        const seen = new Set(merged.map((item) => String(item.id)))
        getFallbackProfessionals().forEach((item) => { if (!seen.has(String(item.id))) merged.push(item) })
        setProfessionals(merged)
        setMessage(list.length ? '' : 'Profissionais carregados em modo local enquanto a API não responde.')
      } catch {
        setProfessionals(getFallbackProfessionals())
        setMessage('Profissionais carregados em modo local enquanto a API não responde.')
      } finally {
        setLoading(false)
      }
    }
    fetchProfessionals()
  }, [])

  const professionalSlots = useMemo(() => Object.fromEntries(professionals.map((professional) => [professional.id, professional.availableSlots || slotsByProfessional[professional.id] || ['09:00', '14:00']])), [professionals])

  const startAppointment = (professional, slot = '10:00') => {
    const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10)
    setSelectedProfessional(professional)
    setForm({ ...initialAnamnesisForm, appointmentDate: tomorrow, appointmentTime: `${slot}:00` })
    setMessage('Agenda aberta. Escolha o horário do profissional e, se desejar, preencha a anamnese antes do atendimento.')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const openOnlineSupport = (professional, forceAnamnesis = false) => {
    const params = new URLSearchParams({
      professional: professional.name,
      professionalId: String(professional.id),
      specialty: professional.specialty,
      title: `Atendimento com ${professional.name}`,
      forceAnamnesis: forceAnamnesis ? '1' : '0'
    })
    navigate(`/atendimento-online?${params.toString()}`)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!selectedProfessional) return

    const payload = buildAppointmentPayload({ form, selectedProfessional, userName: '' })

    try {
      setSubmitting(true)
      const token = localStorage.getItem('token')
      if (token) {
        await api.post('/appointments', payload)
        setMessage('Questionário salvo e anamnese agendada com sucesso.')
      } else {
        saveLocalAppointment(payload)
        setMessage('Agendamento salvo em modo local. Entre depois para sincronizar e concluir o atendimento no portal.')
      }
      setSelectedProfessional(null)
      setForm(initialAnamnesisForm)
    } catch {
      saveLocalAppointment(payload)
      setMessage('API indisponível agora. O agendamento e o questionário foram salvos localmente.')
      setSelectedProfessional(null)
      setForm(initialAnamnesisForm)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <div className="loading-screen">Carregando profissionais...</div>

  return (
    <div className="page-stack">
      <section className="section-title-wrap">
        <span className="eyebrow">Atendimento online especializado</span>
        <h1>Anamnese psicológica inicial integrada ao fluxo do agendamento.</h1>
        <p>Psicólogos e psicopedagogos com foco em matemática, ABA, material dourado, programação e games educativos.</p>
      </section>

      {message ? <div className="alert info">{message}</div> : null}

      <div className="grid cols-3">
        {professionals.map((prof) => (
          <Card key={prof.id}>
            <div className="card-topline">
              <div>
                <h3>{prof.name}</h3>
                <p className="muted strong">{prof.specialty}</p>
              </div>
              {prof.verified ? <span>✅</span> : null}
            </div>
            <p>{prof.specialization}</p>
            <p>{prof.bio}</p>
            <div className="meta-row">
              <span>⭐ {prof.rating} ({prof.reviews} avaliações)</span>
              <strong>R$ {Number(prof.hourlyRate).toFixed(2)}/h</strong>
            </div>
            <div className="stack-sm">
              <strong>Agenda on-line disponível</strong>
              <div className="button-wrap">
                {professionalSlots[prof.id].map((slot) => (
                  <button key={`${prof.id}-${slot}`} type="button" className="chip chip-success" onClick={() => startAppointment(prof, slot)}>{slot}</button>
                ))}
              </div>
            </div>
            <div className="button-wrap">
              <Button onClick={() => startAppointment(prof, professionalSlots[prof.id][0])}>Agendar anamnese online</Button>
              <Button variant="secondary" onClick={() => openOnlineSupport(prof, false)}>Ir para atendimento online</Button>
              <Button variant="ghost" onClick={() => openOnlineSupport(prof, true)}>Solicitar nova anamnese</Button>
            </div>
          </Card>
        ))}
      </div>

      {selectedProfessional ? (
        <form onSubmit={handleSubmit} className="page-stack">
          <UnifiedAnamnesisForm
            form={form}
            setForm={setForm}
            slots={professionalSlots[selectedProfessional.id] || []}
            selectedProfessionalName={selectedProfessional.name}
          />
          <div className="button-wrap">
            <Button type="submit" disabled={submitting}>{submitting ? 'Salvando...' : 'Salvar questionário e agendar'}</Button>
            <Button type="button" variant="secondary" onClick={() => openOnlineSupport(selectedProfessional, false)}>Ir direto para o atendimento</Button>
          </div>
        </form>
      ) : null}
    </div>
  )
}
